import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { BookOpen, Edit, Plus, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
}

function authConfig() {
  const csrf = getCookie("ysq_csrf") || localStorage.getItem("ysq_csrf");
  return { withCredentials: true, headers: csrf ? { "X-CSRF-Token": csrf } : {} };
}

async function ensureCsrf() {
  const response = await axios.get(`${API}/auth/csrf`, { withCredentials: true });
  const token = getCookie("ysq_csrf") || response.data?.csrf_token;
  if (token) localStorage.setItem("ysq_csrf", token);
}

const BLANK = {
  month: 1,
  day: 1,
  reference: "",
  source: "YAH Scriptures",
  text: "",
  source_url: "https://yahscriptures.online/",
  theme: "",
  appointed_time: "",
  is_active: true,
};

export default function DailyScriptureAdmin({ user }) {
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);

  const canManage = (user?.roles || []).includes("admin");

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/daily-scriptures`, authConfig());
      setRecords(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load daily Scripture schedule");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const sorted = useMemo(
    () => [...records].sort((a, b) => (a.month - b.month) || (a.day - b.day)),
    [records]
  );

  const reset = () => {
    setEditingId(null);
    setForm(BLANK);
  };

  const edit = (record) => {
    setEditingId(record.id);
    setForm({
      month: record.month,
      day: record.day,
      reference: record.reference || "",
      source: record.source || "YAH Scriptures",
      text: record.text || "",
      source_url: record.source_url || "",
      theme: record.theme || "",
      appointed_time: record.appointed_time || "",
      is_active: record.is_active !== false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (!form.reference.trim()) {
      toast.error("A Scripture reference is required");
      return;
    }

    try {
      await ensureCsrf();
      const payload = {
        ...form,
        month: Number(form.month),
        day: Number(form.day),
        reference: form.reference.trim(),
        source: form.source.trim() || "YAH Scriptures",
        text: form.text.trim(),
        source_url: form.source_url.trim(),
        theme: form.theme.trim(),
        appointed_time: form.appointed_time.trim(),
      };

      if (editingId) {
        await axios.patch(`${API}/admin/daily-scriptures/${editingId}`, payload, authConfig());
        toast.success("Daily Scripture updated");
      } else {
        await axios.post(`${API}/admin/daily-scriptures`, payload, authConfig());
        toast.success("Daily Scripture scheduled");
      }

      reset();
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save daily Scripture");
    }
  };

  const remove = async (record) => {
    if (!window.confirm(`Remove the Month ${record.month}, Day ${record.day} Scripture override?`)) return;
    try {
      await ensureCsrf();
      await axios.delete(`${API}/admin/daily-scriptures/${record.id}`, authConfig());
      toast.success("Daily Scripture override removed");
      if (editingId === record.id) reset();
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not remove daily Scripture");
    }
  };

  return (
    <div className="space-y-6" data-testid="daily-scripture-admin">
      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            <BookOpen className="w-6 h-6 text-kingdom-gold mt-1" />
            <div>
              <CardTitle>Daily Scripture Schedule</CardTitle>
              <CardDescription className="mt-1">
                The site changes the reading automatically by Abaryt month and day. Saved records override the built-in rotation, including appointed-time readings.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {canManage ? (
            <div className="space-y-5">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label>Abaryt month</Label>
                  <Input type="number" min="1" max="12" value={form.month}
                    onChange={(e) => setForm({ ...form, month: e.target.value })} />
                </div>
                <div>
                  <Label>Abaryt day</Label>
                  <Input type="number" min="1" max="31" value={form.day}
                    onChange={(e) => setForm({ ...form, day: e.target.value })} />
                </div>
                <div className="sm:col-span-2">
                  <Label>Scripture reference</Label>
                  <Input value={form.reference} placeholder="U'YaH'Qara 23:39–44"
                    onChange={(e) => setForm({ ...form, reference: e.target.value })} />
                </div>
                <div>
                  <Label>Source</Label>
                  <Input value={form.source} placeholder="YAH Scriptures"
                    onChange={(e) => setForm({ ...form, source: e.target.value })} />
                </div>
                <div>
                  <Label>Theme</Label>
                  <Input value={form.theme} placeholder="Sakut · rejoice before YaHUaH"
                    onChange={(e) => setForm({ ...form, theme: e.target.value })} />
                </div>
                <div>
                  <Label>Appointed time / remembrance</Label>
                  <Input value={form.appointed_time} placeholder="Sakut"
                    onChange={(e) => setForm({ ...form, appointed_time: e.target.value })} />
                </div>
                <div>
                  <Label>Source link</Label>
                  <Input value={form.source_url} placeholder="https://..."
                    onChange={(e) => setForm({ ...form, source_url: e.target.value })} />
                </div>
              </div>

              <div>
                <Label>Displayed Scripture text <span className="text-slate-500 font-normal">(optional)</span></Label>
                <textarea
                  className="mt-1 w-full min-h-32 rounded-md border border-input bg-background px-3 py-2 text-base"
                  value={form.text}
                  placeholder="Paste the exact wording you want displayed. Leave blank to feature the reading reference and theme only."
                  onChange={(e) => setForm({ ...form, text: e.target.value })}
                />
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                Active
              </label>

              <div className="flex flex-wrap gap-3">
                <Button className="btn-gold" onClick={save}>
                  {editingId ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  {editingId ? "Save changes" : "Schedule Scripture"}
                </Button>
                {editingId && (
                  <Button variant="outline" onClick={reset}>
                    <X className="w-4 h-4 mr-2" /> Cancel edit
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-600">You can view the schedule, but Website / Portal management permission is required to change it.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Manual Overrides</CardTitle>
          <CardDescription>
            If no override is saved for a date, the automatic daily rotation is used. Appointed-time defaults are built in and can also be replaced here.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <p className="p-6 text-slate-500">Loading…</p>
          ) : sorted.length === 0 ? (
            <p className="p-6 text-slate-500">No manual overrides yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="p-3 text-left text-xs font-semibold text-slate-600">Abaryt date</th>
                    <th className="p-3 text-left text-xs font-semibold text-slate-600">Reading</th>
                    <th className="p-3 text-left text-xs font-semibold text-slate-600">Source</th>
                    <th className="p-3 text-left text-xs font-semibold text-slate-600">Purpose</th>
                    <th className="p-3 text-right text-xs font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((record) => (
                    <tr key={record.id} className="border-t">
                      <td className="p-3 font-semibold whitespace-nowrap">Month {record.month} · Day {record.day}</td>
                      <td className="p-3">
                        <div className="font-medium">{record.reference}</div>
                        {record.theme && <div className="text-xs text-slate-500 mt-1">{record.theme}</div>}
                      </td>
                      <td className="p-3 text-sm">{record.source}</td>
                      <td className="p-3 text-sm">{record.appointed_time || "Daily"}</td>
                      <td className="p-3">
                        {canManage && (
                          <div className="flex justify-end gap-2">
                            <Button size="sm" variant="outline" onClick={() => edit(record)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => remove(record)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
